/**
 * DiscountCodeInput Molecule Component
 * Input field with apply button for discount codes
 */

'use client'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { Check, X } from 'lucide-react'
import { useState } from 'react'

interface DiscountCodeInputProps {
	onApply: (code: string) => void
	appliedCode?: string
	validationMessage?: string
	isValid?: boolean
	discountAmount?: number
	className?: string
}

export function DiscountCodeInput({
	onApply,
	appliedCode,
	validationMessage,
	isValid,
	discountAmount,
	className,
}: DiscountCodeInputProps) {
	const [code, setCode] = useState(appliedCode || '')

	const handleApply = () => {
		if (code.trim()) {
			onApply(code.trim())
		}
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleApply()
		}
	}

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat('es-CO', {
			style: 'currency',
			currency: 'COP',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount)
	}

	return (
		<div className={cn('flex flex-col gap-3', className)}>
			<div className='flex flex-col md:flex-row gap-6 items-center bg-[#09194D] p-6 w-full'>
				<label
					htmlFor='discount-code'
					className='font-["Bricolage_Grotesque"] text-2xl font-bold text-[#CDFF00] shrink-0 leading-tight'
				>
					Agregar Código
					<br className='hidden md:block' /> Descuento
				</label>
				<div className='flex gap-4 w-full items-center flex-1'>
					<Input
						id='discount-code'
						type='text'
						value={code}
						onChange={e => setCode(e.target.value.toUpperCase())}
						onKeyDown={handleKeyDown}
						className={cn(
							'border-0 bg-[#E6E6E6] font-["Bricolage_Grotesque"] text-[#09194D] placeholder:text-[#09194D]/40 h-12 rounded-sm flex-1',
							isValid === true && 'ring-2 ring-green-500',
							isValid === false && 'ring-2 ring-red-500'
						)}
					/>
					<Button
						onClick={handleApply}
						disabled={!code.trim()}
						className='bg-transparent border border-[#CDFF00] hover:bg-[#CDFF00] hover:text-[#09194D] text-[#CDFF00] font-["Bricolage_Grotesque"] font-bold text-lg px-8 h-12 rounded-full shrink-0 transition-colors'
					>
						Aplicar
					</Button>
				</div>
			</div>

			{/* Validation feedback */}
			{validationMessage && (
				<div
					className={cn(
						'flex items-center gap-2 px-4 py-3 rounded-md',
						isValid
							? 'bg-green-50 border border-green-200'
							: 'bg-red-50 border border-red-200'
					)}
				>
					{isValid ? (
						<Check className='w-5 h-5 text-green-600 shrink-0' />
					) : (
						<X className='w-5 h-5 text-red-600 shrink-0' />
					)}
					<div className='flex-1'>
						<p
							className={cn(
								'font-["Bricolage_Grotesque"] text-sm font-semibold',
								isValid ? 'text-green-800' : 'text-red-800'
							)}
						>
							{validationMessage}
						</p>
						{isValid && discountAmount && (
							<p className='font-["Bricolage_Grotesque"] text-xl font-black text-green-800 mt-1'>
								Descuento: {formatCurrency(discountAmount)}
							</p>
						)}
					</div>
				</div>
			)}
		</div>
	)
}
